import React from 'react';
import { Box, Button, ButtonGroup, Chip, MenuItem } from '@mui/material';
import * as R from 'ramda';
import PersistentDataGrid from 'common/PersistentDataGrid';
import { arrayOf, bool, func, shape } from 'prop-types';
import { formatDateForExport } from 'utils/helpers/format';
import { format, parseISO } from 'date-fns';
import MDropdownMenuButton from 'common/MDropdownMenuButton';
import { isReConsignmentEditableStatus } from 'feature/shipping/batches/utils';
import { useUserRolePermissions } from 'hooks';
import palette from 'library/theme/palette';

const ReconsignmentCard = props => {
  const {
    loading,
    reconsignments,
    setConfirmDialogType,
    setIsDialogOpen,
    setSelectedReconsignment,
    setModalMode,
  } = props;

  const { isProductOwnerManage, isProductOwnerView } = useUserRolePermissions();

  const handleCopyClick = copyId => {
    setSelectedReconsignment(copyId);
    setModalMode('copy');
  };
  const handleViewClick = viewId => {
    setSelectedReconsignment(viewId);
    setModalMode('view');
  };

  const handleEditClick = editId => {
    setSelectedReconsignment(editId);
    setModalMode('edit');
  };
  const handleCancelClick = cancelId => {
    setIsDialogOpen(true);
    setConfirmDialogType('cancel');
    setSelectedReconsignment(cancelId);
  };

  //  Grid View Implementation of below code
  const minWidth = 150;

  const stripTimeFromDate = date => {
    if (!date) return null;
    const parsedDate = parseISO(date.split('T')[0]);
    return format(parsedDate, 'MM/dd/yyyy');
  };

  const columns = [
    {
      cellClassName: 'css-heap-redact',
      field: 'reconsignmentId',
      headerName: 'Recon. ID',
      minWidth,
    },
    {
      cellClassName: 'css-heap-redact',
      field: 'fromLocation',
      flex: 1,
      headerName: 'From Location',
      minWidth: 200,
    },
    {
      cellClassName: 'css-heap-redact',
      field: 'toLocation',
      flex: 1,
      headerName: 'To Location',
      minWidth: 200,
    },
    {
      cellClassName: 'css-heap-redact',
      field: 'status',
      headerName: 'Status',
      minWidth,
      renderCell: ({ row }) => [
        R.equals('BOOKED', R.prop('status', row)) && (
          <Chip
            color="success"
            key="booked"
            label="Booked"
            sx={{
              '& > .MuiChip-label': { px: 0 },
              borderRadius: 0.5,
              fontSize: 12,
              height: 20,
              px: 1,
              py: 0.5,
              width: 65,
            }}
          />
        ),
        R.equals('CANNOT BOOK', R.prop('status', row)) && (
          <Chip
            color="error"
            key="cannot-book"
            label="Cannot Book"
            sx={{
              '& > .MuiChip-label': { px: 0 },
              borderRadius: 0.5,
              fontSize: 12,
              height: 20,
              px: 1,
              py: 0.5,
              width: 92,
            }}
          />
        ),
        R.equals('SCHEDULED', R.prop('status', row)) && (
          <Chip
            key="scheduled"
            label="Scheduled"
            sx={{
              '& > .MuiChip-label': { px: 0 },
              backgroundColor: palette.success.light,
              borderRadius: 0.5,
              fontSize: 12,
              height: 20,
              maxWidth: 'none',
              px: 1,
              py: 0.5,
              width: 'min-content',
            }}
          />
        ),
        R.equals('EXPIRED', R.prop('status', row)) && (
          <Chip
            color="info"
            key="expired"
            label="Expired"
            sx={{
              '& > .MuiChip-label': { px: 0 },
              borderRadius: 0.5,
              fontSize: 12,
              height: 20,
              px: 1,
              py: 0.5,
              width: 65,
            }}
          />
        ),
        R.equals('CANCELED', R.prop('status', row)) && (
          <Chip
            color="info"
            key="canceled"
            label="Canceled"
            sx={{
              '& > .MuiChip-label': { px: 0 },
              borderRadius: 0.5,
              fontSize: 12,
              height: 20,
              px: 1,
              py: 0.5,
              width: 75,
            }}
          />
        ),
      ],
    },
    {
      cellClassName: 'css-heap-redact',
      field: 'product',
      headerName: 'Product ID',
      minWidth,
    },
    {
      cellClassName: 'css-heap-redact',
      field: 'inventoryOwner',
      headerName: 'Inventory Owner',
      minWidth,
    },
    {
      cellClassName: 'css-heap-redact',
      field: 'originList',
      flex: 1,
      headerName: 'Origin',
      minWidth: 200,
      renderCell: params => (
        <Box
          sx={{
            lineHeight: '1.7em',
            padding: '8px 0',
            whiteSpace: 'normal',
            wordWrap: 'break-word',
          }}
        >
          {params.value}
        </Box>
      ),
    },
    {
      cellClassName: 'css-heap-redact',
      field: 'volume',
      headerName: 'Volume (bbls)',
      minWidth,
      type: 'number',
    },
    {
      cellClassName: 'css-heap-redact',
      field: 'transferDate',
      headerName: 'Transfer Date',
      minWidth,
      renderCell: ({ row }) =>
        R.prop('transferDate', row) ? stripTimeFromDate(R.prop('transferDate', row)) : null,
      valueFormatter: formatDateForExport(),
    },
    {
      cellClassName: 'css-heap-redact',
      field: 'bookedDate',
      headerName: 'Booked Date',
      minWidth,
      renderCell: ({ row }) =>
        R.prop('bookedDate', row)
          ? format(parseISO(R.prop('bookedDate', row)), 'MM/dd/yyyy')
          : null,
      valueFormatter: formatDateForExport(),
    },
    {
      cellClassName: 'css-heap-redact',
      field: 'tooltip',
      headerName: 'Actions',
      minWidth,
      renderCell: params => {
        if (!isProductOwnerManage && isProductOwnerView && R.prop('reconsignmentId', params.row)) {
          return (
            <ButtonGroup orientation="horizontal" sx={{ width: '150px' }} variant="outlined">
              <Button onClick={() => handleViewClick(params.row.reconsignmentId)}>View</Button>
            </ButtonGroup>
          );
        }

        if (isProductOwnerManage && isProductOwnerView) {
          return (
            R.prop('reconsignmentId', params.row) && (
              <Box>
                {!isReConsignmentEditableStatus(params.row.status) && (
                  <ButtonGroup orientation="horizontal" sx={{ width: '150px' }} variant="outlined">
                    <Button onClick={() => handleViewClick(params.row.reconsignmentId)}>
                      View
                    </Button>
                    <MDropdownMenuButton>
                      <MenuItem onClick={() => handleCopyClick(params.row.reconsignmentId)}>
                        Copy
                      </MenuItem>
                    </MDropdownMenuButton>
                  </ButtonGroup>
                )}
                {isReConsignmentEditableStatus(params.row.status) && (
                  <ButtonGroup orientation="horizontal" sx={{ width: '150px' }} variant="outlined">
                    <Button onClick={() => handleEditClick(params.row.reconsignmentId)}>
                      Edit
                    </Button>
                    <MDropdownMenuButton>
                      <MenuItem onClick={() => handleCopyClick(params.row.reconsignmentId)}>
                        Copy
                      </MenuItem>
                      <MenuItem onClick={() => handleCancelClick(params.row.reconsignmentId)}>
                        Cancel
                      </MenuItem>
                    </MDropdownMenuButton>
                  </ButtonGroup>
                )}
              </Box>
            )
          );
        }

        return null;
      },
    },
  ];

  return (
    <PersistentDataGrid
      cellSelection
      columns={columns}
      getRowId={R.prop('reconsignmentId')}
      hideFooter
      id="manualReconsignmentGrid"
      initialState={{
        aggregation: {
          model: {
            volume: 'sum',
          },
        },
      }}
      loading={loading}
      pageSize={10}
      quickFilter
      rows={reconsignments}
    />
  );
};

ReconsignmentCard.propTypes = {
  loading: bool.isRequired,
  reconsignments: arrayOf(shape({})).isRequired,
  setConfirmDialogType: func.isRequired,
  setIsDialogOpen: func.isRequired,
  setModalMode: func.isRequired,
  setSelectedReconsignment: func.isRequired,
};

export default ReconsignmentCard;
